import { type OoxmlChange, isValidOoxml } from '../../core/lib/diffs';

export interface WordDocumentState {
  originalText: string;
  currentText: string;
  hasSelection: boolean;
  selectionText: string;
  ooxml: string; // Full document or selection OOXML
}

export interface ApplyChangeOptions {
  type: 'apply' | 'apply-tracked' | 'apply-comment' | 'apply-tracked-comment';
  comment?: string;
}

export interface TrackedChangeDetail {
  index: number;
  text: string;
  type: string;
  author: string;
}

/**
 * Servicio para interactuar con documentos de Word
 * Maneja documentos OOXML con track changes nativos
 */
export class WordService {
  
  /**
   * Obtiene el estado actual del documento Word (OOXML-based)
   */
  static async getDocumentState(): Promise<WordDocumentState> {
    return new Promise((resolve, reject) => {
      Word.run(async (context) => {
        try {
          const body = context.document.body;
          body.load("text");
          
          const selection = context.document.getSelection();
          selection.load("text");
          
          await context.sync();
          
          const hasSelection = !!selection.text && selection.text.trim().length > 0;
          
          // Get OOXML - either selection or full document
          let ooxmlSource;
          if (hasSelection) {
            console.log('📄 Getting OOXML from selection...');
            ooxmlSource = selection.getOoxml();
          } else {
            console.log('📄 Getting OOXML from full document...');
            ooxmlSource = body.getOoxml();
          }
          
          await context.sync();
          
          const state: WordDocumentState = {
            originalText: body.text || "",
            currentText: body.text || "",
            hasSelection,
            selectionText: selection.text || "",
            ooxml: ooxmlSource.value || ""
          };
          
          console.log('✅ Document state retrieved:', {
            hasSelection,
            textLength: state.currentText.length,
            ooxmlLength: state.ooxml.length
          });
          
          resolve(state);
        } catch (error) {
          console.error('❌ Error getting document state:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Verifica si el documento tiene contenido real (no solo espacios en blanco)
   */
  static async verifyDocumentHasContent(): Promise<boolean> {
    console.log('🔍 Verificando si el documento tiene contenido...');
    return Word.run(async (context) => {
      try {
        const body = context.document.body;
        body.load("text");
        await context.sync();
        const hasContent = body.text.trim().length > 0;
        console.log(`🔍 Verificación de contenido: ${hasContent ? '✅ SÍ tiene' : '❌ NO tiene'}`);
        return hasContent;
      } catch (error) {
        console.error('❌ Error en verifyDocumentHasContent:', error);
        // En caso de error, es más seguro asumir que hay contenido para no bloquear el guardado.
        return true; 
      }
    });
  }

  /**
   * Obtiene el documento completo como archivo .docx (base64)
   */
  static async getFullDocumentAsDocx(): Promise<string> {
    console.log('📦 ========================================');
    console.log('📦 === OBTENIENDO DOCUMENTO COMO .DOCX ===');
    console.log('📦 ========================================');
    console.log('📦 Iniciando Office.context.document.getFileAsync()...');
    
    return new Promise((resolve, reject) => {
      Office.context.document.getFileAsync(
        Office.FileType.Compressed,
        { sliceSize: 4194304 }, // Aumentado a 4MB slices para mayor eficiencia
        async (result) => {
          console.log('📦 getFileAsync callback ejecutado');
          console.log('📦 Estado del resultado:', result.status);
          
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log('✅ getFileAsync exitoso');
            const file = result.value;
            const sliceCount = file.sliceCount;
            const docData: Uint8Array[] = [];
            let totalSize = 0;
            
            console.log(`📦 Documento tiene ${sliceCount} slices`);
            console.log('📦 Leyendo slices como datos binarios...');
            
            // Read all slices
            for (let i = 0; i < sliceCount; i++) {
              try {
                const slice = await new Promise<Office.Slice>((resolveSlice, rejectSlice) => {
                  file.getSliceAsync(i, (sliceResult) => {
                    if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
                      // IMPORTANTE: slice.data es un array de bytes, no texto.
                      const sliceBytes = new Uint8Array(sliceResult.value.data);
                      console.log(`   ✅ Slice ${i + 1} leído exitosamente (${sliceBytes.length} bytes)`);
                      resolveSlice(sliceResult.value);
                    } else {
                      console.error(`   ❌ Error leyendo slice ${i + 1}:`, sliceResult.error);
                      rejectSlice(new Error(`Error reading slice ${i}: ${sliceResult.error.message}`));
                    }
                  });
                });
                
                // Convertir el array de bytes a Uint8Array y almacenarlo
                const sliceBytes = new Uint8Array(slice.data);
                docData.push(sliceBytes);
                totalSize += sliceBytes.length;

              } catch (sliceError) {
                console.error(`❌ Excepción leyendo slice ${i + 1}:`, sliceError);
                file.closeAsync();
                reject(sliceError);
                return;
              }
            }
            
            console.log('📦 Todos los slices leídos exitosamente');
            
            // Combinar todos los Uint8Arrays en un solo array binario
            console.log('📦 Ensamblando slices en un único array binario...');
            const fullDocument = new Uint8Array(totalSize);
            let offset = 0;
            for (const slice of docData) {
              fullDocument.set(slice, offset);
              offset += slice.length;
            }
            console.log(`📦 Ensamblado completo. Tamaño total: ${fullDocument.length} bytes`);

            console.log('📦 Cerrando archivo...');
            file.closeAsync();
            console.log('📦 Archivo cerrado');

            // Convertir el Uint8Array final a base64
            console.log('📦 Codificando array binario a base64...');
            
            // Procesa en chunks para evitar "Maximum call stack size exceeded"
            const CHUNK_SIZE = 32768; // Usar chunks de 32KB
            let binaryString = '';
            for (let i = 0; i < fullDocument.length; i += CHUNK_SIZE) {
              // Usar apply en un chunk es seguro
              binaryString += String.fromCharCode.apply(null, Array.from(fullDocument.subarray(i, i + CHUNK_SIZE)));
            }
            const base64Data = btoa(binaryString);
            
            console.log('✅ ========================================');
            console.log('✅ DOCUMENTO OBTENIDO Y CONVERTIDO A BASE64');
            console.log('✅ ========================================');
            console.log('✅ Total slices:', sliceCount);
            console.log('✅ Tamaño total:', totalSize, 'bytes');
            console.log('✅ Longitud base64:', base64Data.length);
            console.log('✅ Primeros 100 chars:', base64Data.substring(0, 100));
            
            resolve(base64Data);
          } else {
            console.error('❌ ========================================');
            console.error('❌ ERROR EN getFileAsync');
            console.error('❌ ========================================');
            console.error('❌ Estado:', result.status);
            console.error('❌ Error:', result.error);
            console.error('❌ Mensaje:', result.error.message);
            console.error('❌ Código:', result.error.code);
            reject(new Error(`Error getting document file: ${result.error.message}`));
          }
        }
      );
    });
  }

  /**
   * Detecta si el OOXML es un documento completo o un fragmento
   */
  private static isFullDocument(ooxml: string): boolean {
    // Un documento completo tiene pkg:package y document.xml
    const hasPackage = ooxml.includes('<pkg:package>') || ooxml.includes('pkg:package');
    const hasDocument = ooxml.includes('/word/document.xml') || ooxml.includes('<w:document');
    return hasPackage && hasDocument;
  }

  /**
   * Detecta si el OOXML es solo cambios parciales (fragmento)
   */
  private static isFragmentChanges(ooxml: string): boolean {
    // Un fragmento solo tiene párrafos o runs, no la estructura completa
    return !ooxml.includes('<pkg:package>') && 
           (ooxml.includes('<w:p>') || ooxml.includes('<w:r>'));
  }

  /**
   * Envuelve un fragmento OOXML en un documento completo válido
   */
  private static wrapOoxmlFragment(fragment: string): string {
    // Si ya es un documento completo (tiene pkg:package), retornarlo tal cual
    if (fragment.includes('pkg:package')) {
      console.log('✅ OOXML ya es un documento completo, no se envuelve');
      return fragment;
    }

    console.log('🔧 Envolviendo fragmento OOXML en documento válido...');

    // Plantilla de documento OOXML mínimo
    const wrappedOoxml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">
  <pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml">
    <pkg:xmlData>
      <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
        <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
      </Relationships>
    </pkg:xmlData>
  </pkg:part>
  <pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
    <pkg:xmlData>
      <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" 
                  xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml"
                  xmlns:wpc="http://schemas.microsoft.com/office/word/2010/wordprocessingCanvas">
        <w:body>
          ${fragment}
        </w:body>
      </w:document>
    </pkg:xmlData>
  </pkg:part>
</pkg:package>`;

    console.log('✅ Fragmento OOXML envuelto correctamente');
    return wrappedOoxml;
  }

  /**
   * Aplica cambios OOXML con track changes al documento
   * REEMPLAZA TODO EL CONTENIDO DEL DOCUMENTO con el OOXML recibido del backend
   * NOTA: El backend siempre envía el documento completo, no una sección.
   */
  static async applyOoxmlWithTrackChanges(ooxmlString: string): Promise<void> {
    if (!ooxmlString || !ooxmlString.trim()) {
      console.log('📄 No hay OOXML para aplicar');
      return;
    }

    console.log('📝 === APLICANDO OOXML CON TRACK CHANGES ===');
    console.log('📄 OOXML recibido:', ooxmlString.length, 'caracteres');
    console.log('📄 Primeros 200 chars:', ooxmlString.substring(0, 200));
    console.log('📄 Últimos 200 chars:', ooxmlString.substring(ooxmlString.length - 200));
    console.log('📄 Contiene <pkg:package>?', ooxmlString.includes('<pkg:package>'));
    console.log('📄 Contiene <w:ins>?', ooxmlString.includes('<w:ins>'));
    console.log('📄 Es documento completo?', this.isFullDocument(ooxmlString));
    
    // Validar OOXML
    if (!isValidOoxml(ooxmlString)) {
      console.warn('⚠️ OOXML podría no ser válido');
    }

    // Envolver el fragmento en un documento completo si es necesario
    const wrappedOoxml = this.wrapOoxmlFragment(ooxmlString);

    return Word.run(async (context) => {
      try {
        const doc = context.document;
        const body = doc.body;
        
        // 🔑 DESACTIVAR Track Changes antes de insertar
        // Si quedó activo de un cambio anterior, causaría que todo se marque como nuevo
        console.log('🔓 Desactivando modo de Control de Cambios antes de insertar...');
        doc.changeTrackingMode = Word.ChangeTrackingMode.off;
        await context.sync();
        console.log('✅ Control de cambios desactivado temporalmente');
        
        // El OOXML ya viene con las marcas de track changes embebidas
        console.log('📝 Insertando OOXML con track changes embebidos...');
        console.log('📄 OOXML envuelto:', wrappedOoxml.length, 'caracteres');
        
        // SIEMPRE reemplazar TODO el documento
        // El backend envía el documento completo, no fragmentos
        console.log('📍 Reemplazando DOCUMENTO COMPLETO (ignorando selección si existe)');
        
        try {
          // Estrategia 1: Usar range del body completo (más eficiente)
          console.log('🔄 Intentando estrategia 1: Range replace...');
          const bodyRange = body.getRange(Word.RangeLocation.whole);
          bodyRange.insertOoxml(wrappedOoxml, Word.InsertLocation.replace);
          console.log('✅ Estrategia 1: Range replace exitoso');
        } catch (rangeError) {
          console.warn('⚠️ Estrategia 1 falló, intentando estrategia 2 (clear + insert)...', rangeError);
          
          // Estrategia 2 (fallback): Clear + Insert
          console.log('🔄 Ejecutando estrategia 2: Clear + Insert...');
          body.clear();
          await context.sync();
          body.insertOoxml(wrappedOoxml, Word.InsertLocation.start);
          console.log('✅ Estrategia 2: Clear + Insert exitoso');
        }
        
        await context.sync();

        // ❌ NO ACTIVAR trackAll después
        // El OOXML embebido ya tiene las marcas y Word las mostrará automáticamente
        // Si se activa trackAll, futuros reemplazos marcarán todo como nuevo
        console.log('✅ === OOXML APLICADO EXITOSAMENTE ===');
        console.log('✅ Documento reemplazado con el nuevo contenido del backend');
        console.log('✅ Track changes embebidos deberían aparecer correctamente');
        
      } catch (innerError: any) {
        console.error('❌ Error en Word.run:', innerError);
        console.error('❌ Detalles del error:', {
          message: innerError.message,
          code: innerError.code,
          debugInfo: innerError.debugInfo
        });
        throw new Error(`Error aplicando OOXML al documento: ${innerError.message || innerError}`);
      }
    }).catch((error: any) => {
      console.error('❌ Error general aplicando OOXML:', error);
      throw error;
    });
  }

  /**
   * Aplica cambios al documento - NEW OOXML-based implementation
   */
  static async applyChanges(ooxmlChanges: string): Promise<void> {
    return this.applyOoxmlWithTrackChanges(ooxmlChanges);
  }

  /**
   * Acepta todos los track changes del documento
   */
  static async acceptAllTrackChanges(): Promise<void> {
    console.log('✅ === ACEPTANDO TODOS LOS CAMBIOS ===');
    
    return Word.run(async (context) => {
      try {
        const doc = context.document;
        const trackedChanges = doc.body.getTrackedChanges();
        
        // Cargar la colección de cambios
        trackedChanges.load('items');
        await context.sync();
        
        console.log(`📊 Encontrados ${trackedChanges.items.length} cambios rastreados`);
        
        if (trackedChanges.items.length === 0) {
          console.log('ℹ️ No hay cambios rastreados para aceptar');
          return;
        }
        
        // Aceptar cada cambio
        for (const change of trackedChanges.items) {
          change.accept();
        }
        
        await context.sync();
        console.log(`✅ ${trackedChanges.items.length} cambios aceptados exitosamente`);
        
      } catch (error: any) {
        console.error('❌ Error aceptando cambios:', error);
        throw new Error(`Error aceptando cambios: ${error.message || error}`);
      }
    });
  }

  /**
   * Rechaza todos los track changes del documento
   */
  static async rejectAllTrackChanges(): Promise<void> {
    console.log('❌ === RECHAZANDO TODOS LOS CAMBIOS ===');
    
    return Word.run(async (context) => {
      try {
        const doc = context.document;
        const trackedChanges = doc.body.getTrackedChanges();
        
        // Cargar la colección de cambios
        trackedChanges.load('items');
        await context.sync();
        
        console.log(`📊 Encontrados ${trackedChanges.items.length} cambios rastreados`);
        
        if (trackedChanges.items.length === 0) {
          console.log('ℹ️ No hay cambios rastreados para rechazar');
          return;
        }
        
        // Rechazar cada cambio
        for (const change of trackedChanges.items) {
          change.reject();
        }
        
        await context.sync();
        console.log(`✅ ${trackedChanges.items.length} cambios rechazados exitosamente`);
        
      } catch (error: any) {
        console.error('❌ Error rechazando cambios:', error);
        throw new Error(`Error rechazando cambios: ${error.message || error}`);
      }
    });
  }

  /**
   * Obtiene el conteo de track changes pendientes en el documento
   */
  static async getTrackChangesCount(): Promise<number> {
    return Word.run(async (context) => {
      try {
        const doc = context.document;
        const trackedChanges = doc.body.getTrackedChanges();
        
        trackedChanges.load('items');
        await context.sync();
        
        return trackedChanges.items.length;
      } catch (error: any) {
        console.error('❌ Error obteniendo conteo de cambios:', error);
        return 0;
      }
    });
  }

  /**
   * Obtiene el OOXML completo del documento actual
   * Se usa para extraer IDs de cambios y mapear contra TrackedChanges
   */
  static async getDocumentOoxml(): Promise<string> {
    console.log('📄 Obteniendo OOXML del documento...');
    
    return Word.run(async (context) => {
      try {
        const body = context.document.body;
        const ooxml = body.getOoxml();
        await context.sync();
        
        console.log(`✅ OOXML obtenido: ${ooxml.value.length} caracteres`);
        return ooxml.value;
      } catch (error: any) {
        console.error('❌ Error obteniendo OOXML:', error);
        throw new Error(`Error obteniendo OOXML: ${error.message || error}`);
      }
    });
  }

  /**
   * Obtiene todos los TrackedChanges con sus detalles
   */
  static async getAllTrackedChangesDetails(): Promise<TrackedChangeDetail[]> {
    return Word.run(async (context) => {
      try {
        const trackedChanges = context.document.body.getTrackedChanges();
        
        // Cargar propiedades necesarias
        trackedChanges.load('items');
        await context.sync();
        
        const details: TrackedChangeDetail[] = [];
        
        for (let i = 0; i < trackedChanges.items.length; i++) {
          const change = trackedChanges.items[i];
          
          // Cargar propiedades del cambio
          change.load(['text', 'type', 'author']);
        }
        
        await context.sync();
        
        // Recolectar detalles después del sync
        for (let i = 0; i < trackedChanges.items.length; i++) {
          const change = trackedChanges.items[i];
          
          details.push({
            index: i,
            text: change.text || '',
            type: change.type as string,
            author: change.author || ''
          });
        }
        
        return details;
      } catch (error: any) {
        console.error('❌ Error obteniendo detalles de cambios:', error);
        return [];
      }
    });
  }

  /**
   * Acepta un TrackedChange específico por su índice
   */
  static async acceptTrackChangeByIndex(index: number): Promise<void> {
    console.log(`✅ Aceptando cambio en índice ${index}...`);
    
    return Word.run(async (context) => {
      try {
        const trackedChanges = context.document.body.getTrackedChanges();
        trackedChanges.load('items');
        await context.sync();
        
        if (index >= 0 && index < trackedChanges.items.length) {
          trackedChanges.items[index].accept();
          await context.sync();
          console.log(`✅ Cambio ${index} aceptado exitosamente`);
        } else {
          throw new Error(`Índice ${index} fuera de rango (0-${trackedChanges.items.length - 1})`);
        }
      } catch (error: any) {
        console.error(`❌ Error aceptando cambio ${index}:`, error);
        throw new Error(`Error aceptando cambio: ${error.message || error}`);
      }
    });
  }

  /**
   * Rechaza un TrackedChange específico por su índice
   */
  static async rejectTrackChangeByIndex(index: number): Promise<void> {
    console.log(`❌ Rechazando cambio en índice ${index}...`);
    
    return Word.run(async (context) => {
      try {
        const trackedChanges = context.document.body.getTrackedChanges();
        trackedChanges.load('items');
        await context.sync();
        
        if (index >= 0 && index < trackedChanges.items.length) {
          trackedChanges.items[index].reject();
          await context.sync();
          console.log(`✅ Cambio ${index} rechazado exitosamente`);
        } else {
          throw new Error(`Índice ${index} fuera de rango (0-${trackedChanges.items.length - 1})`);
        }
      } catch (error: any) {
        console.error(`❌ Error rechazando cambio ${index}:`, error);
        throw new Error(`Error rechazando cambio: ${error.message || error}`);
      }
    });
  }

  // Obsolete HTML-based methods removed - now using OOXML with native track changes

  /**
   * Aplica cambios con diferentes opciones (OOXML-based)
   */
  static async applyChangesWithOptions(
    ooxmlChanges: string, 
    _options: ApplyChangeOptions
  ): Promise<void> {
    // With OOXML, track changes are embedded in the markup
    // All options use the same method now
    return this.applyChanges(ooxmlChanges);
  }

  // === FUNCIONES DE COMPATIBILIDAD ===

  /**
   * Obtiene todo el texto del documento (compatibilidad)
   */
  static async getFullDocumentText(): Promise<string> {
    return Word.run(async (context) => {
      const doc = context.document;
      doc.load('body');
      await context.sync();
      return doc.body.text;
    });
  }

  /**
   * Obtiene todo el OOXML del documento
   */
  static async getFullDocumentOoxml(): Promise<string> {
    return Word.run(async (context) => {
      const doc = context.document;
      const body = doc.body;
      const ooxml = body.getOoxml();
      await context.sync();
      return ooxml.value;
    });
  }

  /**
   * Obtiene todo el HTML del documento (DEPRECATED - usa getFullDocumentOoxml)
   */
  static async getFullDocumentHtml(): Promise<string> {
    console.warn('⚠️ getFullDocumentHtml is deprecated - use getFullDocumentOoxml instead');
    return this.getFullDocumentOoxml();
  }

  /**
   * Obtiene estadísticas del documento (compatibilidad)
   */
  static async getDocumentStats(): Promise<{ characters: number; words: number; paragraphs: number }> {
    return Word.run(async (context) => {
      const doc = context.document;
      doc.load('body');
      await context.sync();
      
      const body = doc.body;
      const paragraphs = body.paragraphs;
      paragraphs.load('length');
      await context.sync();

      const text = body.text;
      const characters = text.length;
      const words = text.split(/\s+/).filter(word => word.length > 0).length;

      return {
        characters,
        words,
        paragraphs: paragraphs.items.length
      };
    });
  }

  /**
   * Obtiene el contenido actual del documento (compatibilidad con DocumentEditor)
   */
  static async getCurrentDocumentContent(): Promise<string> {
    return this.getFullDocumentText();
  }

  /**
   * Obtiene el contenido seleccionado (compatibilidad)
   */
  static async getSelectedContent(): Promise<string> {
    return Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load('text');
      await context.sync();
      return selection.text;
    });
  }

  /**
   * Propone cambios (compatibilidad)
   */
  static async proposeChanges(_proposal: { changes: string[] }): Promise<void> {
    // Compatibilidad - no implementado
    console.warn('proposeChanges no implementado');
  }

  /**
   * Reemplaza el contenido del documento (compatibilidad)
   */
  static async replaceDocumentContent(newContent: string): Promise<void> {
    return Word.run(async (context) => {
      const doc = context.document;
      doc.body.clear();
      await context.sync();
      doc.body.insertText(newContent, Word.InsertLocation.start);
      await context.sync();
    });
  }

  /**
   * Inserta contenido en la posición del cursor (compatibilidad)
   */
  static async insertContentAtCursor(content: string): Promise<void> {
    return Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.insertText(content, Word.InsertLocation.replace);
      await context.sync();
    });
  }

  /**
   * Carga un documento .docx desde un Blob reemplazando el contenido actual
   * @param blob Blob del archivo .docx
   */
  static async loadDocumentFromBlob(blob: Blob): Promise<void> {
    console.log('📂 ========================================');
    console.log('📂 === CARGANDO DOCUMENTO DESDE BLOB ===');
    console.log('📂 ========================================');
    console.log('📂 Blob size:', blob.size, 'bytes');
    console.log('📂 Blob type:', blob.type);

    return new Promise((resolve, reject) => {
      // Convertir Blob a ArrayBuffer
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          if (!arrayBuffer) {
            throw new Error('No se pudo leer el blob');
          }

          console.log('📂 ArrayBuffer obtenido:', arrayBuffer.byteLength, 'bytes');
          
          // Convertir ArrayBuffer a base64
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const base64 = btoa(binary);
          
          console.log('📂 Base64 generado:', base64.length, 'caracteres');
          console.log('📂 Primeros 100 chars:', base64.substring(0, 100));

          // Usar Word.run para insertar el documento
          await Word.run(async (context) => {
            try {
              const doc = context.document;
              const body = doc.body;
              
              console.log('📂 Limpiando documento actual...');
              body.clear();
              await context.sync();
              
              console.log('📂 Insertando nuevo documento...');
              body.insertFileFromBase64(base64, Word.InsertLocation.start);
              await context.sync();
              
              console.log('✅ ========================================');
              console.log('✅ DOCUMENTO CARGADO EXITOSAMENTE');
              console.log('✅ ========================================');
              
              resolve();
            } catch (wordError: any) {
              console.error('❌ Error en Word.run:', wordError);
              console.error('❌ Detalles:', {
                message: wordError.message,
                code: wordError.code,
                debugInfo: wordError.debugInfo
              });
              reject(new Error(`Error cargando documento en Word: ${wordError.message || wordError}`));
            }
          });
          
        } catch (error: any) {
          console.error('❌ Error procesando blob:', error);
          reject(new Error(`Error procesando blob: ${error.message || error}`));
        }
      };
      
      reader.onerror = (error) => {
        console.error('❌ Error leyendo blob:', error);
        reject(new Error('Error leyendo blob'));
      };
      
      // Iniciar lectura
      console.log('📂 Iniciando lectura del blob...');
      reader.readAsArrayBuffer(blob);
    });
  }

  // Obsolete HTML-based methods removed:
  // - applyContentBasedChanges (no longer needed with OOXML)
  // - applyChangesToDocument (replaced by applyOoxmlWithTrackChanges)
}



